import type { FC, PropsWithChildren } from "react";
import { useParams } from "react-router-dom";
import type { SWRConfiguration } from "swr";

import type { GetInstitutionIdentityByIdResponse } from "src/api/Tmd.dtos";
import InstitutionIdentityContext from "src/contexts/data/InstitutionIdentityContext";
import useInstitutionIdentityById from "src/hooks/data/load/useInstitutionIdentityById";

/**
 * Configures the institution identity request and the descendants receiving its state.
 */
interface IInstitutionProviderProps extends PropsWithChildren {
    id?: number;
    configuration?: SWRConfiguration<
        GetInstitutionIdentityByIdResponse,
        GetInstitutionIdentityByIdResponse
    >;
}

/**
 * Publishes the institution identity loader state to descendants.
 *
 * Postconditions: Children receive the current loader result, including loading and error state;
 * rendering this provider does not guarantee a completed request. Children remain rendered while
 * loading or on failure. The supplied ID and SWR configuration pass through unchanged.
 */
const InstitutionIdentityProvider: FC<IInstitutionProviderProps> = ({
    children,
    id,
    configuration
}) => {
    const context = useInstitutionIdentityById(id, configuration);

    return (
        <InstitutionIdentityContext.Provider value={context}>
            {children}
        </InstitutionIdentityContext.Provider>
    );
};

/**
 * Loads institution identity from the matching route parameter for descendants.
 *
 * Preconditions: Render within the router that supplies the route parameters.
 * Postconditions: An absent or empty parameter becomes undefined; otherwise base-10 parseInt
 * supplies the ID, accepting numeric prefixes and producing NaN for unparseable text.
 * Loading and errors remain in context; this wrapper does not gate children or enforce access.
 * Assumptions: The loader handles request state and the server enforces authorization.
 */
export const RouteInstitutionIdentityProvider: FC<PropsWithChildren> = ({ children }) => {
    const { institutionId } = useParams<{ institutionId: string }>();
    const id = institutionId ? parseInt(institutionId, 10) : undefined;

    return <InstitutionIdentityProvider id={id}>{children}</InstitutionIdentityProvider>;
};

export default InstitutionIdentityProvider;
